/**
 * Accessibility Provider Component
 * Provides keyboard navigation, ARIA live regions, and focus management
 * WCAG AA Compliant
 */

import React, { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AccessibilityContextType {
  announceMessage: (message: string, priority?: 'polite' | 'assertive') => void;
  setPageTitle: (title: string) => void;
  trapFocus: (elementId: string) => void;
  releaseFocus: () => void;
}

const AccessibilityContext = createContext<AccessibilityContextType | null>(null);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within AccessibilityProvider');
  }
  return context;
};

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [liveRegionMessage, setLiveRegionMessage] = useState('');
  const [liveRegionPriority, setLiveRegionPriority] = useState<'polite' | 'assertive'>('polite');
  const [focusTrapElement, setFocusTrapElement] = useState<string | null>(null);
  const [preferredFontSize, setPreferredFontSize] = useState<'normal' | 'large' | 'extra-large'>('normal');
  const [highContrast, setHighContrast] = useState(false);
  const [keyboardNavEnabled, setKeyboardNavEnabled] = useState(true);

  // Initialize accessibility preferences from localStorage
  useEffect(() => {
    const savedPrefs = localStorage.getItem('a11y-preferences');
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      setPreferredFontSize(prefs.fontSize || 'normal');
      setHighContrast(prefs.highContrast || false);
      setKeyboardNavEnabled(prefs.keyboardNav !== false);
    }
    
    // Apply initial settings
    document.documentElement.setAttribute('data-font-size', preferredFontSize);
    document.documentElement.setAttribute('data-high-contrast', highContrast.toString());
  }, []);

  // Announce message to screen readers with enhanced queue management
  const announceMessage = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    // Clear previous message to ensure new one is announced
    setLiveRegionMessage('');
    setTimeout(() => {
      setLiveRegionMessage(message);
      setLiveRegionPriority(priority);
    }, 100);
    
    // Clear message after announcement
    setTimeout(() => {
      setLiveRegionMessage('');
    }, 2000);
  }, []);

  // Set page title for screen readers
  const setPageTitle = useCallback((title: string) => {
    document.title = `${title} - XPROJEX`;
    announceMessage(`Navigated to ${title}`, 'polite');
  }, [announceMessage]);

  // Trap focus within a specific element (for modals/dialogs)
  const trapFocus = useCallback((elementId: string) => {
    setFocusTrapElement(elementId);
  }, []);

  // Release focus trap
  const releaseFocus = useCallback(() => {
    setFocusTrapElement(null);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip to main content (Alt + 1)
      if (event.altKey && event.key === '1') {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.focus();
          mainContent.scrollIntoView({ behavior: 'smooth' });
        }
      }

      // Skip to navigation (Alt + 2)
      if (event.altKey && event.key === '2') {
        const navigation = document.querySelector('[role="navigation"]') as HTMLElement;
        if (navigation) {
          navigation.focus();
        }
      }

      // Open search (Alt + S)
      if (event.altKey && event.key === 's') {
        const searchInput = document.querySelector('[role="search"] input') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }

      // Close modal/dialog (Escape)
      if (event.key === 'Escape') {
        const activeModal = document.querySelector('[role="dialog"][aria-hidden="false"]') as HTMLElement;
        if (activeModal) {
          const closeButton = activeModal.querySelector('[aria-label*="close" i], [aria-label*="dismiss" i]') as HTMLButtonElement;
          if (closeButton) {
            closeButton.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus trap implementation
  useEffect(() => {
    if (!focusTrapElement) return;

    const element = document.getElementById(focusTrapElement);
    if (!element) return;

    const focusableElements = element.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select, [tabindex]:not([tabindex="-1"])'
    );

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusable) {
          e.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        if (document.activeElement === lastFocusable) {
          e.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    element.addEventListener('keydown', handleTabKey);
    firstFocusable?.focus();

    return () => {
      element.removeEventListener('keydown', handleTabKey);
    };
  }, [focusTrapElement]);

  // Add skip link to page - for keyboard/screen reader users
  useEffect(() => {
    const label = t('accessibility.skipToMainContent', 'Skip to main content');
    const existingLink = document.querySelector('.skip-to-main') as HTMLAnchorElement | null;
    const skipLink = existingLink || document.createElement('a');
    const createdByProvider = !existingLink;

    skipLink.href = '#main-content';
    skipLink.className = 'skip-to-main';
    skipLink.textContent = label;
    skipLink.setAttribute('aria-label', label);

    if (createdByProvider) document.body.prepend(skipLink);
    
    return () => {
      if (createdByProvider && document.body.contains(skipLink)) {
        document.body.removeChild(skipLink);
      }
    };
  }, [t]);

  // Ensure main content has proper ID and tabindex
  useEffect(() => {
    // First try to find existing main element
    let mainContent = document.querySelector('main, [role="main"], #main-content') as HTMLElement;
    
    // If no main element, find the main content container (usually first child of root after nav)
    if (!mainContent) {
      const root = document.getElementById('root');
      if (root) {
        // Look for common content containers
        mainContent = root.querySelector('[class*="page-container"], [class*="dashboard"], [class*="content"]') as HTMLElement;
      }
    }
    
    if (mainContent && !mainContent.id) {
      mainContent.id = 'main-content';
      mainContent.tabIndex = -1;
      mainContent.setAttribute('role', 'main');
      mainContent.setAttribute('aria-label', 'Main content');
    }
  }, []);

  // Check for prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleChange = () => {
      if (mediaQuery.matches) {
        document.documentElement.classList.add('reduce-motion');
      } else {
        document.documentElement.classList.remove('reduce-motion');
      }
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply font size and high contrast preferences
  useEffect(() => {
    document.documentElement.setAttribute('data-font-size', preferredFontSize);
    document.documentElement.setAttribute('data-high-contrast', highContrast.toString());
    
    // Save preferences
    localStorage.setItem('a11y-preferences', JSON.stringify({
      fontSize: preferredFontSize,
      highContrast,
      keyboardNav: keyboardNavEnabled
    }));
  }, [preferredFontSize, highContrast, keyboardNavEnabled]);

  return (
    <AccessibilityContext.Provider
      value={{
        announceMessage,
        setPageTitle,
        trapFocus,
        releaseFocus,
      }}
    >
      {children}
      
      {/* ARIA Live Regions for announcements - Absolutely positioned off-screen */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
          padding: 0,
          margin: 0
        }}
      >
        {liveRegionPriority === 'polite' && liveRegionMessage}
      </div>
      
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
          padding: 0,
          margin: 0
        }}
      >
        {liveRegionPriority === 'assertive' && liveRegionMessage}
      </div>
    </AccessibilityContext.Provider>
  );
};

// Accessibility utility functions
export const getAriaLabel = (baseLabel: string, context?: string): string => {
  return context ? `${baseLabel}, ${context}` : baseLabel;
};

export const getAriaDescribedBy = (...ids: (string | undefined)[]): string | undefined => {
  const validIds = ids.filter(Boolean);
  return validIds.length > 0 ? validIds.join(' ') : undefined;
};

// Keyboard navigation hook
export const useKeyboardNavigation = (
  items: any[],
  onSelect: (item: any) => void,
  options?: {
    loop?: boolean;
    orientation?: 'horizontal' | 'vertical';
  }
) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { loop = true, orientation = 'vertical' } = options || {};

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
      const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';

      switch (e.key) {
        case nextKey:
          e.preventDefault();
          setSelectedIndex(prev => {
            const next = prev + 1;
            if (next >= items.length) {
              return loop ? 0 : prev;
            }
            return next;
          });
          break;

        case prevKey:
          e.preventDefault();
          setSelectedIndex(prev => {
            const next = prev - 1;
            if (next < 0) {
              return loop ? items.length - 1 : prev;
            }
            return next;
          });
          break;

        case 'Enter':
        case ' ':
          e.preventDefault();
          if (items[selectedIndex]) {
            onSelect(items[selectedIndex]);
          }
          break;

        case 'Home':
          e.preventDefault();
          setSelectedIndex(0);
          break;

        case 'End':
          e.preventDefault();
          setSelectedIndex(items.length - 1);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [items, selectedIndex, onSelect, loop, orientation]);

  return { selectedIndex, setSelectedIndex };
};

// Focus management hook
export const useFocusManagement = (ref: React.RefObject<HTMLElement>) => {
  const [hasFocus, setHasFocus] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleFocusIn = () => setHasFocus(true);
    const handleFocusOut = () => setHasFocus(false);

    element.addEventListener('focusin', handleFocusIn);
    element.addEventListener('focusout', handleFocusOut);

    return () => {
      element.removeEventListener('focusin', handleFocusIn);
      element.removeEventListener('focusout', handleFocusOut);
    };
  }, [ref]);

  const focusFirst = useCallback(() => {
    if (!ref.current) return;
    const firstFocusable = ref.current.querySelector<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, [ref]);

  const focusLast = useCallback(() => {
    if (!ref.current) return;
    const focusableElements = ref.current.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );
    const lastFocusable = focusableElements[focusableElements.length - 1];
    lastFocusable?.focus();
  }, [ref]);

  return { hasFocus, focusFirst, focusLast };
};

export default AccessibilityProvider;
